import { useState, createContext, useContext, useMemo, useEffect } from "react";
import { useLocation, Navigate } from "react-router-dom";
import { getRefreshTokenCookie, removeRefreshTokenCookie } from "lib/Storage";
import { useGetMyProfile } from "hooks/user";
import Loading from "components/shared/loading";
import { useGetEvent, useGetEventDetail } from "./event";

export const InfoContext = createContext(null);

export function InfoProvider({ children }) {
  const { mutate: getMyProfile } = useGetMyProfile();
  const [userInfo, setUserInfo] = useState(null);
  const [editUserInfo, setEditUserInfo] = useState({ edit: false });
  const [loading, setLoading] = useState(false);
  const [isLogin, setIsLogin] = useState(!!getRefreshTokenCookie());
  const [error, setError] = useState(null);
  const [nextPage, setNextPage] = useState(null);
  const [prePage, setPrePage] = useState(null);
  const [currentPage, setCurrentPage] = useState(null);
  const [saveChangesModal, setSaveChangesModal] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  const { mutate: getEvent } = useGetEvent();
  const { mutate: getEventDetail } = useGetEventDetail();
  const [itemEvent, setItemEvent] = useState();
  const [itemEventDetail, setItemEventDetail] = useState();
  const [repToken, setRepToken] = useState();
  const [userIdRegistration, setUserIdRegistration] = useState();

  useEffect(() => {
    if (!isLogin) return;
    setLoading(true);
    getMyProfile(
      {},
      {
        onSuccess: ({ status, user }) => {
          if (status === "OK" && user) {
            setUserInfo(user);
          } else {
            removeRefreshTokenCookie();
            setIsLogin(false);
          }
          setLoading(false);
        },
        onError: (e) => {
          setError(e);
        },
      }
    );
  }, [getMyProfile, isLogin]);

  useEffect(() => {
    if (isLogin) {
      getEvent(
        {},
        {
          onSuccess: (response) => {
            if (response.status === "OK" && response.events?.length > 0) {
              const event = response.events[0];
              setItemEvent(event);

              getEventDetail(event._id, {
                onSuccess: (detail) => {
                  setItemEventDetail(detail?.event);
                },
                onError: (error) => {
                  console.error("Error fetching event detail:", error);
                },
              });
            }
          },
          onError: (e) => {
            console.error(e);
          },
        }
      );
    }
  }, [getEvent, getEventDetail, isLogin]);

  const memoedValue = useMemo(() => {
    return {
      userInfo,
      setUserInfo,
      isLogin,
      setIsLogin,
      loading,
      setLoading,
      error,
      setError,
      nextPage,
      setNextPage,
      prePage,
      setPrePage,
      currentPage,
      setCurrentPage,
      editUserInfo,
      setEditUserInfo,
      saveChangesModal,
      setSaveChangesModal,
      showSidebar,
      setShowSidebar,
      itemEvent,
      setItemEvent,
      itemEventDetail,
      setItemEventDetail,
      repToken,
      setRepToken,
      userIdRegistration,
      setUserIdRegistration,
    };
  }, [
    userInfo,
    setUserInfo,
    isLogin,
    setIsLogin,
    loading,
    setLoading,
    error,
    setError,
    nextPage,
    setNextPage,
    prePage,
    setPrePage,
    currentPage,
    setCurrentPage,
    editUserInfo,
    setEditUserInfo,
    saveChangesModal,
    setSaveChangesModal,
    showSidebar,
    setShowSidebar,
    itemEvent,
    itemEventDetail,
    repToken,
    setRepToken,
    userIdRegistration,
    setUserIdRegistration,
  ]);

  return (
    <InfoContext.Provider value={memoedValue}>{children}</InfoContext.Provider>
  );
}

export default function useInfoContext() {
  return useContext(InfoContext);
}

export function RequireAuth({ children }) {
  const { isLogin, loading } = useInfoContext();
  const location = useLocation();

  if (loading) {
    return <Loading />;
  }

  if (!isLogin) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}
